export interface Rect {
    left: number;
    top: number;
    width: number;
    height: number;
}

export interface PopoverSize {
    width: number;
    height: number;
}

export interface PopoverBounds {
    top: number;
    bottom: number;
    left: number;
    right: number;
}

export interface PopoverPosition {
    left: number;
    top: number;
    transform: string;
}

const GAP = 12;

/**
 * Pure placement math for the anchored popover. No DOM access — the caller
 * supplies the anchor's rect (already read via getBoundingClientRect),
 * the popover's own size, and the stage bounds it must stay inside.
 *
 * Placement preference: above the anchor; flip below if it would cross
 * `bounds.top`; if neither fits, clamp vertically inside the bounds.
 * Horizontal position is always clamped so the card never leaves the stage.
 */
export function computePopoverPlacement(anchor: Rect, size: PopoverSize, bounds: PopoverBounds): PopoverPosition {
    const anchorCenterX = anchor.left + anchor.width / 2;
    const halfWidth = size.width / 2;
    const boundsWidth = bounds.right - bounds.left;
    // Clamping to [left + halfWidth, right - halfWidth] inverts once the
    // popover is wider than the bounds: the lower bound overtakes the upper
    // one and Math.max wins, pushing the card off the right edge. Centre it
    // instead, which is the best available placement on a viewport too
    // narrow to hold it.
    const left =
        size.width >= boundsWidth
            ? (bounds.left + bounds.right) / 2
            : Math.max(bounds.left + halfWidth, Math.min(bounds.right - halfWidth, anchorCenterX));

    const fitsAbove = anchor.top - GAP - size.height >= bounds.top;
    const fitsBelow = anchor.top + anchor.height + GAP + size.height <= bounds.bottom;

    if (fitsAbove) {
        return { left, top: anchor.top - GAP, transform: 'translate(-50%, -100%)' };
    }

    if (fitsBelow) {
        return { left, top: anchor.top + anchor.height + GAP, transform: 'translate(-50%, 0)' };
    }

    const top = Math.max(bounds.top, Math.min(bounds.bottom - size.height, anchor.top));
    return { left, top, transform: 'translate(-50%, 0)' };
}
